import { Box } from "@mui/material";
import React from "react";
import { createScopedLogger } from "../../../common";
import LoadingOverlay from "../../../common/components/LoadingOverlay";
import { VariableNames } from "../../Constants/VariableNames";
import { useChildContexts } from "../../Hooks/useChildContexts";
import { useComponentSelection } from "../../Hooks/useComponentSelection";
import useObservedExpression from "../../Hooks/useObservedExpression";
import useObservedText from "../../Hooks/useObservedText";
import { RepeatContainerModel } from "../../Models/RepeatContainerModel";
import ComponentFactory from "../ComponentFactory";
import IComponentProps from "../IComponentProps";
import getComponentLayout from "../Table/getComponentLayout";

const logger = createScopedLogger("RepeatContainerComponent");

const RepeatContainerComponent: React.FC<IComponentProps<RepeatContainerModel>> = ({ component, context, componentPath = [] }) => {
  const componentLayoutSx = getComponentLayout(component);

  const color = useObservedText(component.Color, context, "RepeatContainerComponent");
  const backgroundColor = useObservedText(component.BackgroundColor, context, "RepeatContainerComponent");

  const [items, itemsPending] = useObservedExpression<any[]>(component.Items, context, "RepeatContainerComponent", (value, error) => {
    if (error || value === undefined || value === null) {
      return [];
    }

    if (!Array.isArray(value)) {
      logger.error("Items expression did not return an array:", value);
      throw new Error("Items expression did not return an array: " + value);
    }

    return value;
  });

  // Create child contexts for each item (cached by key)
  const contextItems = useChildContexts(context, items, component.ItemKeyField, component.ItemVariableName ?? VariableNames.Item);

  const selectionProps = useComponentSelection({
    component,
    componentPath,
    isContainer: true,
    isEmpty: !items || items.length === 0,
  });

  return (
    <Box sx={{ position: "relative", ...componentLayoutSx, color, backgroundColor }} {...selectionProps}>
      <LoadingOverlay loading={itemsPending} />
      {contextItems?.map(({ key, context: itemContext }, itemIndex) => {
        return (
          <React.Fragment key={`${itemContext.StableId}-${key}`}>
            {component.Children.map((child, childIndex) => (
              <ComponentFactory
                parent={component}
                key={`${itemContext.StableId}-${childIndex}-${child.Id}`}
                component={child}
                context={itemContext}
                componentPath={[...componentPath, child.Id]}
              />
            ))}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default RepeatContainerComponent;
